/**
 * Shared zod helpers + brand coercions used across the schemas package.
 *
 * Stays internal — not re-exported from `src/index.ts`.
 */
import { z } from 'zod';
/** Cuid2 strings (24 chars, lowercase alphanumeric). Permissive on length to
 *  tolerate test fixtures + future cuid2 length changes. */
export const idString = z.string().min(1).max(64);
/** Branded id schema — typed cast to a `Brand<string, B>`. */
export const brandedId = () => idString;
/** Unix-epoch milliseconds — what data-model.md persists for every timestamp. */
export const timestampMs = z
    .number()
    .int()
    .nonnegative()
    .or(z.coerce.date().transform((d) => d.getTime()));
/** Trimmed non-empty string of bounded length (sane UX defaults). */
export const trimmedString = (max = 255) => z.string().trim().min(1).max(max);
/** Optional trimmed string — empty becomes undefined. */
export const optionalTrimmed = (max = 255) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? undefined : v))
    .optional();
/** Email field with standard max length per RFC 5321. */
export const emailField = z.string().trim().email().max(254);
/** Hex color (6 or 8 digits with optional alpha). */
export const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/, 'invalid hex color');
/** URL field — empty string allowed, becomes undefined. */
export const urlOrEmpty = z
    .string()
    .trim()
    .max(2048)
    .refine((v) => v === '' || isValidUrl(v), { message: 'invalid url' });
function isValidUrl(value) {
    try {
        new URL(value);
        return true;
    }
    catch {
        return false;
    }
}
/** IANA timezone string (loose validation — full check requires Intl). */
export const ianaTimezone = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z_/+\-0-9]+$/, 'invalid IANA timezone');
/** YYYY-MM-DD date string. */
export const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
/** ISO week string YYYY-Www. */
export const isoWeekString = z.string().regex(/^\d{4}-W\d{2}$/, 'expected YYYY-Www');
/** HH:00 hour-of-day string. */
export const hourString = z.string().regex(/^([01]\d|2[0-3]):00$/, 'expected HH:00');
//# sourceMappingURL=_helpers.js.map